import { Box, Typography } from '@mui/material';
import {
  DateCalendar,
  DateCalendarSlotsComponentsProps,
} from '@mui/x-date-pickers/DateCalendar';
import { DayCalendarSkeleton } from '@mui/x-date-pickers/DayCalendarSkeleton';
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { endOfMonth, format, startOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';

import TimelineCalendarDay, {
  CALENDAR_DAY_PITCH,
  CALENDAR_DAY_SIZE,
  TimelineCalendarDayExtraProps,
} from './TimelineCalendarDay';
import { TimelineDateRange } from '../calendarTypes';
import { fromCivilDate, toCivilDate } from '../timelineCalendarDates';

export const formatCalendarMonth = (month: Date) => {
  const label = format(month, 'MMMM yyyy', { locale: ptBR });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

function TimelineCalendarMonthHeader({
  currentMonth,
  labelId,
}: PickersCalendarHeaderProps<Date>) {
  return (
    <Box sx={{ px: 0.5, pb: 0.5 }}>
      <Typography id={labelId} component="h3" variant="body2" fontWeight={600}>
        {formatCalendarMonth(currentMonth)}
      </Typography>
    </Box>
  );
}

interface TimelineCalendarMonthProps {
  month: Date;
  minDate: string;
  eventDays: Set<string>;
  range: TimelineDateRange;
  loading: boolean;
  /** Semanas a reservar, para alinhar a altura dos meses lado a lado. */
  weeks: number;
  onSelectDay: (day: string) => void;
}

export default function TimelineCalendarMonth({
  month,
  minDate,
  eventDays,
  range,
  loading,
  weeks,
  onSelectDay,
}: TimelineCalendarMonthProps) {
  const monthStart = startOfMonth(month);
  const earliestDate = fromCivilDate(minDate);
  const dayProps: TimelineCalendarDayExtraProps = { eventDays, range };

  return (
    <DateCalendar<Date>
      data-testid={`timeline-calendar-month-${format(monthStart, 'yyyy-MM')}`}
      value={null}
      referenceDate={monthStart > earliestDate ? monthStart : earliestDate}
      views={['day']}
      minDate={monthStart > earliestDate ? monthStart : earliestDate}
      maxDate={endOfMonth(monthStart)}
      showDaysOutsideCurrentMonth={false}
      reduceAnimations
      disableHighlightToday
      loading={loading}
      renderLoading={() => <DayCalendarSkeleton />}
      onChange={(day) => {
        if (day) onSelectDay(toCivilDate(day));
      }}
      slots={{
        calendarHeader: TimelineCalendarMonthHeader,
        day: TimelineCalendarDay,
      }}
      slotProps={{
        // Props extras são repassadas pelo slot ao TimelineCalendarDay.
        day: dayProps as DateCalendarSlotsComponentsProps<Date>['day'],
      }}
      sx={{
        width: 7 * CALENDAR_DAY_PITCH,
        maxWidth: '100%',
        height: 'auto',
        maxHeight: 'none',
        m: 0,
        '& .MuiDayCalendar-weekDayLabel': {
          width: CALENDAR_DAY_SIZE,
          height: 24,
          mx: 0.25,
          fontWeight: 600,
        },
        '& .MuiPickersDay-hiddenDaySpacingFiller': {
          width: CALENDAR_DAY_SIZE,
          height: CALENDAR_DAY_SIZE,
        },
        '& .MuiDayCalendar-slideTransition, & .MuiDayCalendar-loadingContainer':
          { minHeight: weeks * CALENDAR_DAY_PITCH },
      }}
    />
  );
}
